import React, { useState, useEffect, useRef } from 'react';
import { createVisualRecord, updateVisualRecord, fetchPacientes, fetchVisualRecords, fetchNextVisualFolio, fetchHistorialCambiosVisual, anularVisualRecord, createPaciente, updatePaciente } from '../../servicios/apiClinica';
import { HojaEstilos, HojaDatalists, HojaEncabezado, Linea, RxFinalTabla, EspecificacionesLentes, INK, exportarHojaPDF, ErroresHoja, AvisoErrores } from './HojaVisualFormato';
import { validarFormula, advertenciasClinicas, normalizarCampo, potenciaLenteContacto } from './optometria';

// "RX EN USO" de un examen nuevo = RX final del examen anterior (una línea por ojo).
const rxFinalComoTexto = (rec, ojo) => {
    if (!rec) return '';
    const partes = [rec[`esfera_${ojo}`], rec[`cilindro_${ojo}`], rec[`eje_${ojo}`] ? `x ${rec[`eje_${ojo}`]}°` : '', rec[`add_${ojo}`] ? `ADD ${rec[`add_${ojo}`]}` : ''];
    return partes.filter(Boolean).join(' ');
};
const rxEnUsoDesde = (rec) => {
    const od = rxFinalComoTexto(rec, 'od');
    const oi = rxFinalComoTexto(rec, 'oi');
    return od || oi ? `${od}\n${oi}` : '';
};
// Fecha de nacimiento real del paciente; la estimada desde la edad (1 de enero) no se muestra como dato cierto.
const fechaNacimientoReal = (p) => (p?.birth_date && !p?.birth_date_estimada ? p.birth_date : '');
const edadDesde = (fechaIso) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(fechaIso || '');
    if (!m) return '';
    const hoy = new Date();
    let edad = hoy.getFullYear() - +m[1];
    if (hoy.getMonth() + 1 < +m[2] || (hoy.getMonth() + 1 === +m[2] && hoy.getDate() < +m[3])) edad--;
    return edad >= 0 ? `${edad}` : '';
};
const ultimoRegistro = (recs) => [...(recs || [])].filter(r => !r.anulada).sort((a, b) => new Date(b.date || b.created_at) - new Date(a.date || a.created_at))[0] || null;

// `initialRecord`: historia existente que se abre para consultar o corregir.
// `previousRecord`: último examen del paciente; solo precarga "RX en uso" y "último control" de un examen NUEVO.
const HistoriaClinicaVisual = ({ patient = null, initialRecord = null, previousRecord = null, onSaved, onCancel, onNuevoExamen, isViewOnly: soloLecturaInicial = false }) => {
    // En modo lectura se consulta la ficha tal como se guardó; "Editar" la habilita para corregirla.
    // Una historia anulada siempre se abre en solo lectura: se conserva tal como quedó.
    const [isViewOnly, setIsViewOnly] = useState(soloLecturaInicial || !!initialRecord?.anulada);
    const [anulacion, setAnulacion] = useState(initialRecord?.anulada ? {
        motivo: initialRecord.motivo_anulacion,
        fecha: initialRecord.fecha_anulacion
    } : null);
    const printRef = useRef(null);
    const user = JSON.parse(localStorage.getItem('user')) || {};
    const defaultDoctor = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username || 'Optometrista';

    const [selectedPatient, setSelectedPatient] = useState(patient || null);
    const [pacientesList, setPacientesList] = useState([]);

    useEffect(() => {
        if (!patient) {
            fetchPacientes().then(data => setPacientesList(data || []));
        } else {
            setSelectedPatient(patient);
        }
    }, [patient]);

    const getToday = () => new Date().toISOString().split('T')[0];

    const getInitialDate = (months) => {
        const d = new Date();
        d.setMonth(d.getMonth() + months);
        return d.toISOString().split('T')[0];
    };

    const [form, setForm] = useState({
        id: initialRecord?.id || null,
        folio_number: initialRecord?.folio_number || '',
        date: initialRecord?.date || getToday(),
        last_control_date: initialRecord ? (initialRecord.last_control_date || '') : (previousRecord?.date || patient?.last_checkup_date || ''),
        next_control_date: initialRecord?.next_control_date || patient?.next_checkup_date || getInitialDate(12),
        reason_for_consultation: initialRecord?.reason_for_consultation || patient?.reason_for_consultation || '',
        
        // Paciente Info
        nombres: patient?.first_name || '',
        apellidos: patient?.last_name || '',
        cedula: patient?.cedula || '',
        edad: patient?.age !== undefined && patient?.age !== null ? `${patient.age}` : '',
        fecha_nacimiento: fechaNacimientoReal(patient),
        telefono: patient?.phone || '',
        correo: patient?.email || '',
        direccion: patient?.address || '',
        ocupacion: patient?.occupation || '',

        // 1. Agudeza Visual & Refracción en Uso
        av_sc_od_lejos: initialRecord?.av_sc_od_lejos || '',
        av_sc_od_cerca: initialRecord?.av_sc_od_cerca || '',
        av_sc_oi_lejos: initialRecord?.av_sc_oi_lejos || '',
        av_sc_oi_cerca: initialRecord?.av_sc_oi_cerca || '',
        rx_en_uso: initialRecord ? (initialRecord.rx_en_uso || '') : rxEnUsoDesde(previousRecord),
        av_cc_od_lejos: initialRecord?.av_cc_od_lejos || '',
        av_cc_od_cerca: initialRecord?.av_cc_od_cerca || '',
        av_cc_oi_lejos: initialRecord?.av_cc_oi_lejos || '',
        av_cc_oi_cerca: initialRecord?.av_cc_oi_cerca || '',

        // 2. Exámenes
        examen_externo_od: initialRecord?.examen_externo_od || '',
        examen_externo_oi: initialRecord?.examen_externo_oi || '',
        queratometria_od: initialRecord?.queratometria_od || '',
        queratometria_oi: initialRecord?.queratometria_oi || '',
        retinoscopia_od: initialRecord?.retinoscopia_od || '',
        retinoscopia_oi: initialRecord?.retinoscopia_oi || '',
        ppc: initialRecord?.ppc || '',
        cover_test: initialRecord?.cover_test || '',
        add_val: initialRecord?.add_val || '',
        
        // Prueba LC
        lc_od_cb: initialRecord?.lentes_contacto_data?.od_cb || '',
        lc_od_diam: initialRecord?.lentes_contacto_data?.od_diam || '',
        lc_od_pot: initialRecord?.lentes_contacto_data?.od_pot || '',
        lc_oi_cb: initialRecord?.lentes_contacto_data?.oi_cb || '',
        lc_oi_diam: initialRecord?.lentes_contacto_data?.oi_diam || '',
        lc_oi_pot: initialRecord?.lentes_contacto_data?.oi_pot || '',
        lc_observaciones: initialRecord?.lentes_contacto_data?.observaciones || '',
        lc_od_obs: initialRecord?.lentes_contacto_data?.od_obs || '',
        lc_oi_obs: initialRecord?.lentes_contacto_data?.oi_obs || '',

        // 3. RX Final
        esfera_od: initialRecord?.esfera_od || '',
        cilindro_od: initialRecord?.cilindro_od || '',
        eje_od: initialRecord?.eje_od || '',
        add_od: initialRecord?.add_od || '',
        avl_od: initialRecord?.avl_od || '',
        avc_od: initialRecord?.avc_od || '',

        esfera_oi: initialRecord?.esfera_oi || '',
        cilindro_oi: initialRecord?.cilindro_oi || '',
        eje_oi: initialRecord?.eje_oi || '',
        add_oi: initialRecord?.add_oi || '',
        avl_oi: initialRecord?.avl_oi || '',
        avc_oi: initialRecord?.avc_oi || '',

        // 4. Especificaciones Lentes y Montura
        tipo_lentes: initialRecord?.tipo_lentes || '',
        color: initialRecord?.color || '',
        bifocal: initialRecord?.bifocal || '',
        tratamiento: initialRecord?.tratamiento || '',
        dp: initialRecord?.dp || '',
        alt: initialRecord?.alt || '',
        diam: initialRecord?.diam || '',
        observaciones: initialRecord?.observaciones || '',
        profesional: initialRecord?.profesional || defaultDoctor,

        // 5. Evaluación complementaria
        antecedentes: initialRecord?.antecedentes || '',
        pio_od: initialRecord?.pio_od || '',
        pio_oi: initialRecord?.pio_oi || '',
        fondo_ojo_od: initialRecord?.fondo_ojo_od || '',
        fondo_ojo_oi: initialRecord?.fondo_ojo_oi || '',
        diagnostico: initialRecord?.diagnostico || '',
        conducta: initialRecord?.conducta || ''
    });
    const [folioSugerido, setFolioSugerido] = useState('');
    // Tras un intento de guardar fallido, la validación se recalcula en cada cambio: la marca roja desaparece al corregir.
    const [intentoGuardar, setIntentoGuardar] = useState(false);
    const [erroresServidor, setErroresServidor] = useState({});
    // Historial de cambios de esta ficha (null = panel cerrado)
    const [cambios, setCambios] = useState(null);
    const handleAnular = async () => {
        const motivo = window.prompt(
            `¿Anular la historia Nº ${form.folio_number}?\n\n` +
            'La historia NO se borra: queda guardada y visible, marcada como ANULADA.\n\n' +
            'Escriba el motivo de la anulación:'
        );
        if (motivo === null) return;
        if (!motivo.trim()) {
            alert('Debe indicar el motivo de la anulación.');
            return;
        }
        try {
            const reg = await anularVisualRecord(form.id, motivo.trim());
            setAnulacion({ motivo: reg.motivo_anulacion, fecha: reg.fecha_anulacion });
            setIsViewOnly(true);
            if (onSaved) onSaved(reg, { cerrar: false });
        } catch (error) {
            alert('No se pudo anular:\n\n' + error.message);
        }
    };

    const abrirCambios = async () => {
        setCambios('cargando');
        setCambios(await fetchHistorialCambiosVisual(form.id));
    };

    useEffect(() => {
        if (!initialRecord) fetchNextVisualFolio().then(setFolioSugerido);
    }, [initialRecord]);

    const [saving, setSaving] = useState(false);
    const [printing, setPrinting] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (erroresServidor[name]) {
            setErroresServidor(prev => { const next = { ...prev }; delete next[name]; return next; });
        }
        setForm(prev => {
            const next = { ...prev, [name]: value };
            if (name === 'fecha_nacimiento') next.edad = edadDesde(value) || prev.edad;
            // La casilla "Add" de la hoja y la columna ADD del RX final son el mismo dato: se mantienen sincronizados.
            if (name === 'add_val') {
                ['add_od', 'add_oi'].forEach(k => {
                    if (!prev[k] || prev[k] === prev.add_val) next[k] = value;
                });
            }
            return next;
        });
    };

    // Al salir de un campo óptico se normaliza el formato (ej: "-2" → "-2.00", eje 0 → 180).
    const handleBlurNormalize = (e) => {
        const { name, value } = e.target || {};
        if (!name || isViewOnly) return;
        const normalizado = normalizarCampo(name, value);
        if (normalizado !== value) setForm(prev => ({ ...prev, [name]: normalizado }));
    };

    const handleSetCheckupMonths = (months) => {
        setForm(prev => ({ ...prev, next_control_date: getInitialDate(months) }));
    };

    const handleSelectPatientDropdown = (pId) => {
        const p = pacientesList.find(x => String(x.id) === String(pId));
        if (p) {
            setSelectedPatient(p);
            setForm(prev => ({
                ...prev,
                nombres: p.first_name || '',
                apellidos: p.last_name || '',
                cedula: p.cedula || '',
                edad: p.age !== undefined && p.age !== null ? `${p.age}` : '',
                fecha_nacimiento: fechaNacimientoReal(p),
                telefono: p.phone || '',
                correo: p.email || '',
                direccion: p.address || '',
                ocupacion: p.occupation || '',
                last_control_date: p.last_checkup_date || '',
                next_control_date: getInitialDate(12)
            }));
            if (!form.id) {
                fetchVisualRecords(p.id).then(recs => {
                    const ultimo = ultimoRegistro(recs);
                    if (ultimo) {
                        setForm(prev => ({ ...prev, last_control_date: ultimo.date || prev.last_control_date, rx_en_uso: prev.rx_en_uso || rxEnUsoDesde(ultimo) }));
                    }
                });
            }
        }
    };

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();

        const { datos, campos } = validarFormula(form);
        if (Object.keys(campos).length) {
            setIntentoGuardar(true);
            enfocarCampo(Object.keys(campos)[0]);
            return;
        }
        setIntentoGuardar(false);
        const correo = (form.correo || '').trim().toLowerCase();
        if (correo && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(correo)) {
            setErroresServidor({ correo: 'El correo no es válido. Ejemplo: nombre@gmail.com' });
            enfocarCampo('correo');
            return;
        }
        const nacimiento = form.fecha_nacimiento || '';
        // Opcional: sin fecha, la edad la estima como 1 de enero (marcada como estimada, no se felicita).
        const edadValida = /^\d{1,3}$/.test((form.edad || '').trim());
        const nacimientoEstimado = edadValida ? `${new Date().getFullYear() - parseInt(form.edad, 10)}-01-01` : null;
        if (nacimiento && (!edadDesde(nacimiento) || nacimiento < '1900-01-01')) {
            setErroresServidor({ fecha_nacimiento: 'La fecha de nacimiento no es válida.' });
            enfocarCampo('fecha_nacimiento');
            return;
        }
        setErroresServidor({});
        setForm(datos);
        const avisos = advertenciasClinicas(datos);
        if (avisos.length && !window.confirm('Hallazgos a revisar:\n\n• ' + avisos.join('\n• ') + '\n\n¿Guardar de todas formas?')) {
            return;
        }

        setSaving(true);
        try {
            let targetPatientId = selectedPatient?.id || patient?.id;

            if (!targetPatientId) {
                // Si no hay paciente seleccionado, verificar si se ingresaron datos en la ficha física
                const nombreVal = (form.nombres || '').trim();
                const apellidoVal = (form.apellidos || '').trim();
                const cedulaVal = (form.cedula || '').trim();

                if (!nombreVal && !apellidoVal && !cedulaVal) {
                    alert('Por favor ingrese al menos el nombre o cédula del paciente en la ficha para guardarlo.');
                    setSaving(false);
                    return;
                }

                const newPatientData = {
                    first_name: nombreVal || 'Paciente',
                    last_name: apellidoVal || 'Sin Apellido',
                    cedula: cedulaVal || null,
                    phone: (form.telefono || '').trim(),
                    address: (form.direccion || '').trim(),
                    occupation: (form.ocupacion || '').trim(),
                    email: correo || null,
                    birth_date: nacimiento || nacimientoEstimado,
                    birth_date_estimada: !nacimiento && !!nacimientoEstimado,
                    last_checkup_date: form.last_control_date || null,
                    next_checkup_date: form.next_control_date || null,
                    is_clinical: true
                };

                let createdPatient;
                try {
                    createdPatient = await createPaciente(newPatientData);
                } catch (createErr) {
                    if (/email/i.test(createErr.message)) {
                        setErroresServidor({ correo: 'Ese correo ya está registrado en otro paciente.' });
                        enfocarCampo('correo');
                        setSaving(false);
                        return;
                    }
                    throw createErr;
                }
                targetPatientId = createdPatient.id;
                setSelectedPatient(createdPatient);
            } else {
                // Sincronizar cambios personales en el paciente existente
                try {
                    const updateData = {
                        first_name: (form.nombres || '').trim() || selectedPatient?.first_name,
                        last_name: (form.apellidos || '').trim() || selectedPatient?.last_name,
                        cedula: (form.cedula || '').trim() || selectedPatient?.cedula || null,
                        phone: (form.telefono || '').trim() || selectedPatient?.phone,
                        address: (form.direccion || '').trim() || selectedPatient?.address,
                        occupation: (form.ocupacion || '').trim() || selectedPatient?.occupation,
                        email: correo || selectedPatient?.email || null
                    };
                    // Una fecha de nacimiento escrita reemplaza a la estimada. La edad sola solo estima la fecha
                    // (marcada como estimada, no se usa para felicitar) cuando el paciente no tiene ninguna.
                    // Último/próximo control los actualiza el servidor a partir del examen más reciente.
                    if (nacimiento && nacimiento !== fechaNacimientoReal(selectedPatient)) {
                        updateData.birth_date = nacimiento;
                        updateData.birth_date_estimada = false;
                    } else if (!selectedPatient?.birth_date && nacimientoEstimado) {
                        updateData.birth_date = nacimientoEstimado;
                        updateData.birth_date_estimada = true;
                    }
                    await updatePaciente(targetPatientId, updateData);
                } catch (syncErr) {
                    // El correo es único por paciente: si choca, se avisa en el campo y no se guarda.
                    if (/email/i.test(syncErr.message)) {
                        setErroresServidor({ correo: 'Ese correo ya está registrado en otro paciente.' });
                        enfocarCampo('correo');
                        setSaving(false);
                        return;
                    }
                    console.warn('Sincronización paciente aviso:', syncErr);
                }
            }

            const payload = {
                patient: targetPatientId,
                folio_number: datos.folio_number,
                last_control_date: datos.last_control_date || null,
                next_control_date: datos.next_control_date || null,
                reason_for_consultation: datos.reason_for_consultation,
                
                av_sc_od_lejos: datos.av_sc_od_lejos,
                av_sc_od_cerca: datos.av_sc_od_cerca,
                av_sc_oi_lejos: datos.av_sc_oi_lejos,
                av_sc_oi_cerca: datos.av_sc_oi_cerca,
                rx_en_uso: datos.rx_en_uso,
                av_cc_od_lejos: datos.av_cc_od_lejos,
                av_cc_od_cerca: datos.av_cc_od_cerca,
                av_cc_oi_lejos: datos.av_cc_oi_lejos,
                av_cc_oi_cerca: datos.av_cc_oi_cerca,

                examen_externo_od: datos.examen_externo_od,
                examen_externo_oi: datos.examen_externo_oi,
                queratometria_od: datos.queratometria_od,
                queratometria_oi: datos.queratometria_oi,
                retinoscopia_od: datos.retinoscopia_od,
                retinoscopia_oi: datos.retinoscopia_oi,
                ppc: datos.ppc,
                cover_test: datos.cover_test,
                add_val: datos.add_val,
                lentes_contacto_data: {
                    od_cb: datos.lc_od_cb,
                    od_diam: datos.lc_od_diam,
                    od_pot: datos.lc_od_pot,
                    oi_cb: datos.lc_oi_cb,
                    oi_diam: datos.lc_oi_diam,
                    oi_pot: datos.lc_oi_pot,
                    observaciones: datos.lc_observaciones,
                    od_obs: datos.lc_od_obs,
                    oi_obs: datos.lc_oi_obs
                },

                esfera_od: datos.esfera_od,
                cilindro_od: datos.cilindro_od,
                eje_od: datos.eje_od,
                add_od: datos.add_od,
                avl_od: datos.avl_od,
                avc_od: datos.avc_od,

                esfera_oi: datos.esfera_oi,
                cilindro_oi: datos.cilindro_oi,
                eje_oi: datos.eje_oi,
                add_oi: datos.add_oi,
                avl_oi: datos.avl_oi,
                avc_oi: datos.avc_oi,

                tipo_lentes: datos.tipo_lentes,
                color: datos.color,
                bifocal: datos.bifocal,
                tratamiento: datos.tratamiento,
                dp: datos.dp,
                alt: datos.alt,
                diam: datos.diam,
                observaciones: datos.observaciones,
                profesional: datos.profesional,
                date: datos.date || null,
                antecedentes: datos.antecedentes,
                pio_od: datos.pio_od,
                pio_oi: datos.pio_oi,
                fondo_ojo_od: datos.fondo_ojo_od,
                fondo_ojo_oi: datos.fondo_ojo_oi,
                diagnostico: datos.diagnostico,
                conducta: datos.conducta
            };

            let res;
            if (datos.id) {
                res = await updateVisualRecord(datos.id, payload);
            } else {
                res = await createVisualRecord(payload);
            }

            if (res?.id) setForm(prev => ({ ...prev, id: res.id, folio_number: res.folio_number || prev.folio_number }));
            alert(`¡Historia Clínica Visual Nº ${res?.folio_number || datos.folio_number} guardada con éxito!`);
            if (onSaved) onSaved(res);
        } catch (error) {
            console.error('Error guardando historia visual:', error);
            if (error.campos && Object.keys(error.campos).length) {
                setErroresServidor(error.campos);
                enfocarCampo(Object.keys(error.campos)[0]);
            } else {
                alert('No se pudo guardar:\n\n' + (error.message || 'Verifique los datos'));
            }
        } finally {
            setSaving(false);
        }
    };

    const enfocarCampo = (name) => {
        setTimeout(() => {
            const el = document.querySelector(`#visual-clinical-sheet [name="${name}"]`);
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                el.focus({ preventScroll: true });
            }
        }, 50);
    };

    const handlePrintPDF = async () => {
        setPrinting(true);
        try {
            const nombre = `${form.nombres || patient?.first_name || ''}_${form.apellidos || patient?.last_name || ''}`.trim();
            await exportarHojaPDF(document.getElementById('visual-clinical-sheet'), `Historia_Visual_${form.folio_number || folioSugerido}_${nombre}.pdf`);
        } catch (err) {
            console.error('Error generando PDF:', err);
            window.print();
        } finally {
            setPrinting(false);
        }
    };

    const rxUsoLines = (form.rx_en_uso || '').split('\n');
    const handleRxUsoChange = (idx) => (e) => {
        const lines = [rxUsoLines[0] || '', rxUsoLines[1] || ''];
        lines[idx] = e.target.value.replace(/\n/g, ' ');
        setForm(prev => ({ ...prev, rx_en_uso: lines[1] ? lines.join('\n') : lines[0] }));
    };

    const edit = isViewOnly ? undefined : handleChange;
    const erroresCampos = { ...(intentoGuardar ? validarFormula(form).campos : {}), ...erroresServidor };
    const edadCalculada = !!form.fecha_nacimiento || !!selectedPatient?.birth_date;
    const folioMostrado = form.folio_number || folioSugerido;
    const lcSugerido = ['od', 'oi'].map(ojo => potenciaLenteContacto(form[`esfera_${ojo}`], form[`cilindro_${ojo}`]));

    const LensArc = () => (
        <svg width="64" height="36" viewBox="0 0 64 36">
            <path d="M5,15 Q32,-3 59,15" stroke={INK} strokeWidth="1.2" fill="none" />
            <path d="M5,21 Q32,39 59,21" stroke={INK} strokeWidth="1.2" fill="none" />
        </svg>
    );

    // Render de la hoja clínica física (copia del formato impreso Vision Click)
    const renderSheet = () => (
        <ErroresHoja.Provider value={erroresCampos}>
        <div id="visual-clinical-sheet" ref={printRef} className="hv-sheet" onBlur={handleBlurNormalize}>
            <HojaEstilos />
            <HojaDatalists />
            <HojaEncabezado folio={form.folio_number} folioPlaceholder={folioSugerido} onFolioChange={edit} readOnly={isViewOnly} />
            <AvisoErrores errores={erroresCampos} />
            {anulacion && (
                <>
                    <div className="hv-anulada-banner">
                        <strong>HISTORIA ANULADA</strong> el {anulacion.fecha ? new Date(anulacion.fecha).toLocaleString() : '—'}.
                        {' '}Motivo: {anulacion.motivo || '—'}. Se conserva como registro; no tiene validez clínica.
                    </div>
                    <div className="hv-anulada-sello">ANULADA</div>
                </>
            )}

            <div className="hv-body">
                <div className="hv-frame">
                    {/* Datos generales y del paciente */}
                    <div className="hv-top">
                        <div className="hv-row-patient">
                            <Linea label="APELLIDOS:" name="apellidos" value={form.apellidos} onChange={edit} inputStyle={{ fontWeight: 700 }} />
                            <Linea label="NOMBRES:" name="nombres" value={form.nombres} onChange={edit} inputStyle={{ fontWeight: 700 }} />
                        </div>
                        <div className="hv-row4">
                            <Linea label="C.I.:" name="cedula" value={form.cedula} onChange={edit} />
                            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '4px' }}>
                                <Linea label="EDAD:" name="edad" value={form.edad} onChange={edit} readOnly={edadCalculada} inputStyle={{ textAlign: 'center' }} style={{ flex: 1 }} />
                                <span className="hv-label" style={{ paddingBottom: '2px' }}>AÑOS</span>
                            </div>
                            <Linea label="ÚLTIMO CONTROL:" name="last_control_date" type="date" value={form.last_control_date} onChange={edit} />
                            <Linea label="FECHA:" name="date" type="date" value={form.date} onChange={edit} />
                        </div>
                        <div className="hv-row2">
                            <Linea label="DIRECCIÓN:" name="direccion" value={form.direccion} onChange={edit} />
                            <Linea label="CORREO:" name="correo" type="email" value={form.correo} onChange={edit} placeholder="Para recordatorios" />
                        </div>
                        <div className="hv-row3">
                            <Linea label="TELÉFONO:" name="telefono" value={form.telefono} onChange={edit} />
                            <Linea label="F. NACIMIENTO:" name="fecha_nacimiento" type="date" value={form.fecha_nacimiento} onChange={edit} />
                            <Linea label="OCUPACIÓN:" name="ocupacion" value={form.ocupacion} onChange={edit} />
                        </div>
                        <div className="hv-line" style={{ alignItems: 'flex-start', marginBottom: 0 }}>
                            <span className="hv-label" style={{ lineHeight: '20px' }}>MOTIVO DE CONSULTA:</span>
                            {isViewOnly ? (
                                <div className="hv-lined" style={{ minHeight: '40px' }}>{form.reason_for_consultation}</div>
                            ) : (
                                <textarea name="reason_for_consultation" value={form.reason_for_consultation} onChange={handleChange} rows={2} className="hv-lined" />
                            )}
                        </div>
                    </div>

                    {/* AV SC / RX EN USO / AV CC */}
                    <div className="hv-av-wrap">
                        <table className="hv-grid">
                            <colgroup>
                                <col style={{ width: '10%' }} /><col style={{ width: '11%' }} /><col style={{ width: '12%' }} />
                                <col style={{ width: '33%' }} /><col style={{ width: '15%' }} /><col style={{ width: '13%' }} />
                            </colgroup>
                            <thead>
                                <tr>
                                    <th rowSpan="2"></th>
                                    <th colSpan="2">AV.SC.</th>
                                    <th rowSpan="2" style={{ verticalAlign: 'top', paddingTop: '6px' }}>RX EN USO</th>
                                    <th colSpan="2">AV.CC.</th>
                                </tr>
                                <tr>
                                    <th>LEJOS</th><th>CERCA</th><th>LEJOS</th><th>CERCA</th>
                                </tr>
                            </thead>
                            <tbody>
                                {['od', 'oi'].map((ojo, idx) => (
                                    <tr key={ojo}>
                                        <td className="hv-eye">{ojo === 'od' ? 'O.D' : 'O.I'}</td>
                                        {[`av_sc_${ojo}_lejos`, `av_sc_${ojo}_cerca`].map(n => (
                                            <td key={n}><input name={n} value={form[n]} onChange={edit} readOnly={isViewOnly} list={n.endsWith('cerca') ? 'hv-av-cerca-list' : 'hv-av-list'} className="hv-cell-input" /></td>
                                        ))}
                                        <td><input value={rxUsoLines[idx] || ''} onChange={handleRxUsoChange(idx)} readOnly={isViewOnly} className="hv-cell-input" /></td>
                                        {[`av_cc_${ojo}_lejos`, `av_cc_${ojo}_cerca`].map(n => (
                                            <td key={n}><input name={n} value={form[n]} onChange={edit} readOnly={isViewOnly} list={n.endsWith('cerca') ? 'hv-av-cerca-list' : 'hv-av-list'} className="hv-cell-input" /></td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Exámenes (izq.) + Add / Prueba Lentes de Contacto (der.) */}
                    <div className="hv-mid">
                        <div>
                            <div className="hv-sub" style={{ marginTop: '14px' }}>EXAMEN EXTERNO</div>
                            {['od', 'oi'].map(ojo => (
                                <div key={ojo} className="hv-line" style={{ alignItems: 'flex-start' }}>
                                    <span className="hv-label" style={{ width: '30px', fontSize: '11px', lineHeight: '20px' }}>{ojo === 'od' ? 'O.D' : 'O.I'}</span>
                                    {isViewOnly ? (
                                        <div className="hv-lined" style={{ minHeight: '40px' }}>{form[`examen_externo_${ojo}`]}</div>
                                    ) : (
                                        <textarea name={`examen_externo_${ojo}`} value={form[`examen_externo_${ojo}`]} onChange={handleChange} rows={2} className="hv-lined" />
                                    )}
                                </div>
                            ))}

                            <div className="hv-sub">QUERATOMETRÍA</div>
                            <Linea label="O.D" name="queratometria_od" value={form.queratometria_od} onChange={edit} labelStyle={{ width: '30px', fontSize: '11px' }} />
                            <Linea label="O.I" name="queratometria_oi" value={form.queratometria_oi} onChange={edit} labelStyle={{ width: '30px', fontSize: '11px' }} />

                            <div className="hv-sub">RETINOSCOPIA</div>
                            <Linea label="O.D" name="retinoscopia_od" value={form.retinoscopia_od} onChange={edit} labelStyle={{ width: '30px', fontSize: '11px' }} />
                            <Linea label="O.I" name="retinoscopia_oi" value={form.retinoscopia_oi} onChange={edit} labelStyle={{ width: '30px', fontSize: '11px' }} />
                            <Linea label="P.P.C" name="ppc" value={form.ppc} onChange={edit} labelStyle={{ width: '30px', fontSize: '11px' }} />
                            <Linea label="COVER TEST:" name="cover_test" value={form.cover_test} onChange={edit} style={{ marginTop: '10px' }} />
                        </div>

                        <div>
                            <div className="hv-add" style={{ marginTop: '6px' }}>
                                <span className="hv-label" style={{ paddingTop: '2px' }}>Add:</span>
                                <input name="add_val" value={form.add_val} onChange={edit} readOnly={isViewOnly} className={`hv-add-box${erroresCampos.add_val ? ' hv-error' : ''}`} title={erroresCampos.add_val || undefined} />
                                {erroresCampos.add_val && <span className="hv-asterisco">*</span>}
                                <span className="hv-lc-title" style={{ paddingTop: '6px' }}>Prueba Lentes Contacto</span>
                            </div>
                            <div className="hv-lc-arcs" style={{ marginLeft: '92px' }}>
                                <span style={{ fontSize: '11px' }}>O.D</span><span style={{ fontSize: '11px' }}>O.I</span>
                                <LensArc /><LensArc /><LensArc /><LensArc />
                            </div>

                            {['od', 'oi'].map(ojo => (
                                <div key={ojo} className="hv-line">
                                    <span className="hv-label" style={{ width: '30px', fontSize: '11px' }}>{ojo === 'od' ? 'O.D' : 'O.I'}</span>
                                    <input name={`lc_${ojo}_cb`} value={form[`lc_${ojo}_cb`]} onChange={edit} readOnly={isViewOnly} placeholder="CB" className="hv-input" style={{ flex: 0.8 }} />
                                    <input name={`lc_${ojo}_diam`} value={form[`lc_${ojo}_diam`]} onChange={edit} readOnly={isViewOnly} placeholder="DIA" className="hv-input" style={{ flex: 0.8 }} />
                                    <input name={`lc_${ojo}_pot`} value={form[`lc_${ojo}_pot`]} onChange={edit} readOnly={isViewOnly} placeholder="POT" className="hv-input" />
                                </div>
                            ))}

                            {lcSugerido.some(Boolean) && (
                                <div className="hv-hint" data-html2canvas-ignore="true">
                                    Potencia LC compensada por distancia al vértice (12 mm):
                                    {lcSugerido[0] && <> O.D <strong>{lcSugerido[0]}</strong></>}
                                    {lcSugerido[1] && <> · O.I <strong>{lcSugerido[1]}</strong></>}
                                </div>
                            )}
                            <div style={{ height: lcSugerido.some(Boolean) ? '50px' : '78px' }} />
                            <Linea label="O.D" name="lc_od_obs" value={form.lc_od_obs} onChange={edit} labelStyle={{ width: '30px', fontSize: '11px' }} />
                            <Linea label="O.I" name="lc_oi_obs" value={form.lc_oi_obs} onChange={edit} labelStyle={{ width: '30px', fontSize: '11px' }} style={{ marginTop: '10px' }} />
                        </div>
                    </div>

                    <RxFinalTabla data={form} onChange={edit} readOnly={isViewOnly} />
                    <EspecificacionesLentes data={form} onChange={edit} readOnly={isViewOnly} />

                    <div className="hv-sign">
                        <div className="hv-sign-box">
                            <span className="hv-sign-caption">PROFESIONAL</span>
                            <Linea label="NOMBRES:" name="profesional" value={form.profesional} onChange={edit} inputStyle={{ fontWeight: 700, textAlign: 'center' }} />
                        </div>
                    </div>
                </div>
            </div>

            {/* Evaluación complementaria: no está en la hoja de papel, pero es necesaria para decidir derivaciones */}
            <div className="hv-extra">
                <div className="hv-extra-title">EVALUACIÓN COMPLEMENTARIA</div>
                <div className="hv-line" style={{ alignItems: 'flex-start' }}>
                    <span className="hv-label" style={{ lineHeight: '20px' }}>ANTECEDENTES:</span>
                    {isViewOnly ? (
                        <div className="hv-lined" style={{ minHeight: '40px' }}>{form.antecedentes}</div>
                    ) : (
                        <textarea name="antecedentes" value={form.antecedentes} onChange={handleChange} rows={2} className="hv-lined" placeholder="Diabetes, hipertensión, glaucoma familiar, cirugías, medicación..." />
                    )}
                </div>
                <div className="hv-extra-grid">
                    <Linea label="PIO O.D (mmHg):" name="pio_od" value={form.pio_od} onChange={edit} />
                    <Linea label="PIO O.I (mmHg):" name="pio_oi" value={form.pio_oi} onChange={edit} />
                    <Linea label="FONDO DE OJO O.D:" name="fondo_ojo_od" value={form.fondo_ojo_od} onChange={edit} />
                    <Linea label="FONDO DE OJO O.I:" name="fondo_ojo_oi" value={form.fondo_ojo_oi} onChange={edit} />
                </div>
                <Linea label="DIAGNÓSTICO:" name="diagnostico" value={form.diagnostico} onChange={edit} placeholder="Ej: Miopía OU, astigmatismo miópico compuesto OD, presbicia" />
                <Linea label="CONDUCTA / DERIVACIÓN:" name="conducta" value={form.conducta} onChange={edit} placeholder="Ej: Lentes de uso permanente; derivar a oftalmología por PIO elevada" />
            </div>

            {/* Próximo control: dato del sistema, no forma parte de la hoja impresa */}
            <div data-html2canvas-ignore="true" className="d-print-none" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px', padding: '8px 12px', marginLeft: '34px', background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: '8px', fontSize: '12px' }}>
                <strong style={{ color: '#047857' }}>PRÓXIMO CONTROL:</strong>
                <input type="date" name="next_control_date" value={form.next_control_date} onChange={edit} readOnly={isViewOnly} style={{ border: 'none', borderBottom: '1.5px solid #10b981', background: 'transparent', outline: 'none', fontWeight: 'bold', color: '#047857' }} />
                {!isViewOnly && (
                    <>
                        <button type="button" onClick={() => handleSetCheckupMonths(6)} style={{ fontSize: '11px', padding: '1px 8px', border: '1px solid #a7f3d0', borderRadius: '4px', background: '#fff', cursor: 'pointer', fontWeight: 600 }}>+6m</button>
                        <button type="button" onClick={() => handleSetCheckupMonths(12)} style={{ fontSize: '11px', padding: '1px 8px', border: '1px solid #a7f3d0', borderRadius: '4px', background: '#fff', cursor: 'pointer', fontWeight: 600 }}>+1 año</button>
                    </>
                )}
            </div>
        </div>
        </ErroresHoja.Provider>
    );

    // Si onCancel está presente, se renderiza como diálogo modal ergonómico
    if (onCancel) {
        return (
            <div 
                style={{
                    width: '100%',
                    maxWidth: '920px',
                    maxHeight: '94vh',
                    backgroundColor: '#ffffff',
                    borderRadius: '16px',
                    boxShadow: '0 25px 50px -12px rgba(0,0,0,0.4)',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    position: 'relative'
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {cambios !== null && (
                    <div style={{ position: 'absolute', inset: 0, background: 'rgba(17,24,39,0.45)', zIndex: 5, display: 'flex', justifyContent: 'flex-end' }} onClick={() => setCambios(null)}>
                        <div style={{ width: '440px', maxWidth: '100%', height: '100%', background: '#fff', display: 'flex', flexDirection: 'column', boxShadow: '-8px 0 24px rgba(0,0,0,0.15)' }} onClick={(e) => e.stopPropagation()}>
                            <div style={{ padding: '14px 18px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <strong><i className="bi bi-clock-history me-2"></i>Historial de cambios · Nº {folioMostrado}</strong>
                                <button type="button" onClick={() => setCambios(null)} style={{ border: 'none', background: 'transparent', fontSize: '1.3rem', cursor: 'pointer' }}>&times;</button>
                            </div>
                            <div style={{ padding: '14px 18px', overflowY: 'auto', flex: 1, fontSize: '0.85rem' }}>
                                {cambios === 'cargando' ? 'Cargando...' : cambios.length === 0 ? (
                                    <div style={{ color: '#6b7280' }}>Esta historia no ha sido modificada desde que se guardó.</div>
                                ) : cambios.map(c => (
                                    <div key={c.id} style={{ borderLeft: `3px solid ${c.accion === 'editar' ? '#2563eb' : '#dc2626'}`, padding: '6px 10px', marginBottom: '12px', background: '#f9fafb', borderRadius: '0 6px 6px 0' }}>
                                        <div style={{ fontWeight: 700 }}>
                                            {c.accion === 'editar' ? 'Edición' : c.accion === 'anular' ? 'Anulación' : 'Intento de eliminación (bloqueado)'}
                                            <span style={{ fontWeight: 400, color: '#6b7280' }}> · {new Date(c.fecha).toLocaleString()} · {c.usuario || 'Sin usuario'}</span>
                                        </div>
                                        {c.cambios.map(x => (
                                            <div key={x.campo} style={{ marginTop: '3px' }}>
                                                <span style={{ color: '#374151', fontWeight: 600 }}>{x.campo.replace(/_/g, ' ').toUpperCase()}:</span>{' '}
                                                <span style={{ color: '#991b1b', textDecoration: 'line-through' }}>{typeof x.antes === 'object' && x.antes !== null ? JSON.stringify(x.antes) : (x.antes || '(vacío)')}</span>
                                                {' → '}
                                                <span style={{ color: '#166534', fontWeight: 600 }}>{typeof x.despues === 'object' && x.despues !== null ? JSON.stringify(x.despues) : (x.despues || '(vacío)')}</span>
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
                {/* Barra de Encabezado Modal Fija */}
                <div 
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '12px 20px',
                        borderBottom: '1px solid #e5e7eb',
                        backgroundColor: '#ffffff',
                        flexShrink: 0
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: '800', color: '#111827', fontSize: '1.05rem' }}>
                            <i className="bi bi-eye-fill me-2"></i> Historia Clínica Visual
                        </span>
                        <span style={{ backgroundColor: '#dc2626', color: '#fff', padding: '2px 8px', borderRadius: '6px', fontSize: '0.82rem', fontWeight: 'bold' }}>
                            Nº {folioMostrado}
                        </span>
                        {selectedPatient ? (
                            <span style={{ backgroundColor: '#f3f4f6', color: '#111827', padding: '2px 10px', borderRadius: '6px', fontSize: '0.82rem', fontWeight: '600' }}>
                                {selectedPatient.first_name} {selectedPatient.last_name} ({selectedPatient.cedula || 'S/C'})
                            </span>
                        ) : (
                            <select 
                                className="form-select form-select-sm" 
                                style={{ minWidth: '220px', maxWidth: '300px', fontWeight: '600', borderRadius: '8px', border: '1.5px solid #111827', fontSize: '0.82rem' }}
                                value={selectedPatient?.id || ''}
                                onChange={(e) => handleSelectPatientDropdown(e.target.value)}
                            >
                                <option value="">-- Paciente Nuevo / Seleccionar --</option>
                                {pacientesList.map(p => (
                                    <option key={p.id} value={p.id}>
                                        {p.first_name} {p.last_name} ({p.cedula || 'S/C'})
                                    </option>
                                ))}
                            </select>
                        )}
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        {anulacion ? (
                            <span style={{ backgroundColor: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca', padding: '3px 10px', borderRadius: '999px', fontSize: '0.78rem', fontWeight: 700 }}>
                                <i className="bi bi-slash-circle me-1"></i>ANULADA
                            </span>
                        ) : isViewOnly && (
                            <span style={{ backgroundColor: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', padding: '3px 10px', borderRadius: '999px', fontSize: '0.78rem', fontWeight: 700 }}>
                                <i className="bi bi-lock me-1"></i>Solo lectura
                            </span>
                        )}
                        {form.id && onNuevoExamen && (
                            <button
                                type="button"
                                onClick={onNuevoExamen}
                                className="btn btn-sm btn-outline-dark d-flex align-items-center gap-1"
                                style={{ borderRadius: '8px', fontWeight: '600' }}
                                title="Registrar un examen nuevo; esta historia queda guardada sin cambios"
                            >
                                <i className="bi bi-plus-lg"></i>
                                <span>Nuevo examen</span>
                            </button>
                        )}
                        {form.id && (
                            <button
                                type="button"
                                onClick={abrirCambios}
                                className="btn btn-sm btn-outline-dark d-flex align-items-center gap-1"
                                style={{ borderRadius: '8px', fontWeight: '600' }}
                                title="Ver quién cambió qué y los valores anteriores"
                            >
                                <i className="bi bi-clock-history"></i>
                                <span>Cambios</span>
                            </button>
                        )}
                        {form.id && !anulacion && (
                            <button
                                type="button"
                                onClick={handleAnular}
                                className="btn btn-sm d-flex align-items-center gap-1"
                                style={{ borderRadius: '8px', fontWeight: '600', border: '1.5px solid #dc2626', color: '#dc2626', background: '#fff' }}
                                title="Anular esta historia (no se borra, queda marcada como ANULADA)"
                            >
                                <i className="bi bi-slash-circle"></i>
                                <span>Anular</span>
                            </button>
                        )}
                        {isViewOnly && !anulacion && (
                            <button
                                type="button"
                                onClick={() => setIsViewOnly(false)}
                                className="btn btn-sm btn-outline-dark d-flex align-items-center gap-1"
                                style={{ borderRadius: '8px', fontWeight: '600' }}
                                title="Corregir esta historia"
                            >
                                <i className="bi bi-pencil"></i>
                                <span>Editar</span>
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={handlePrintPDF}
                            disabled={printing}
                            className="btn btn-sm btn-outline-dark d-flex align-items-center gap-1"
                            style={{ borderRadius: '8px', fontWeight: '600' }}
                        >
                            <i className="bi bi-printer"></i>
                            <span>{printing ? 'Generando...' : 'Imprimir / PDF'}</span>
                        </button>
                        {!isViewOnly && (
                            <button
                                type="button"
                                onClick={handleSubmit}
                                disabled={saving}
                                className="btn btn-sm text-white d-flex align-items-center gap-1 shadow-sm"
                                style={{ backgroundColor: '#111827', color: '#ffffff', border: '1px solid #111827', borderRadius: '8px', fontWeight: '700' }}
                            >
                                <i className="bi bi-check-lg" style={{ color: '#ffffff' }}></i>
                                <span style={{ color: '#ffffff' }}>{saving ? 'Guardando...' : 'Guardar Ficha'}</span>
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={onCancel}
                            className="btn btn-sm btn-light border"
                            style={{ borderRadius: '8px', padding: '3px 9px', fontSize: '1.1rem', lineHeight: 1 }}
                            title="Cerrar ventana"
                        >
                            &times;
                        </button>
                    </div>
                </div>

                {/* Contenedor con Scroll de la Hoja Clínica */}
                <div 
                    style={{
                        overflowY: 'auto',
                        flex: 1,
                        padding: '20px',
                        backgroundColor: '#f3f4f6',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'flex-start'
                    }}
                >
                    {renderSheet()}
                </div>
            </div>
        );
    }

    // Modo Vista Completa (Ruta independiente /clinica/historia-visual)
    return (
        <div style={{ backgroundColor: '#f3f4f6', minHeight: '100vh', padding: '24px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {/* Barra de Acciones Superior */}
            <div style={{ width: '100%', maxWidth: '850px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', background: '#ffffff', padding: '12px 20px', borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: '800', color: '#111827', fontSize: '1.1rem' }}>
                        <i className="bi bi-eye-fill me-2"></i> Historia Clínica Visual
                    </span>
                    <span style={{ backgroundColor: '#dc2626', color: '#fff', padding: '2px 8px', borderRadius: '6px', fontSize: '0.85rem', fontWeight: 'bold' }}>
                        Nº {folioMostrado}
                    </span>
                    {!patient && (
                        <select 
                            className="form-select form-select-sm ms-2" 
                            style={{ minWidth: '220px', maxWidth: '320px', fontWeight: 'bold', borderRadius: '8px', border: '1.5px solid #111827' }}
                            value={selectedPatient?.id || ''}
                            onChange={(e) => handleSelectPatientDropdown(e.target.value)}
                        >
                            <option value="">-- Seleccionar Paciente --</option>
                            {pacientesList.map(p => (
                                <option key={p.id} value={p.id}>
                                    {p.first_name} {p.last_name} ({p.cedula || 'S/C'})
                                </option>
                            ))}
                        </select>
                    )}
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                    <button 
                        type="button" 
                        onClick={handlePrintPDF} 
                        disabled={printing}
                        className="btn btn-sm btn-outline-dark d-flex align-items-center gap-1"
                        style={{ borderRadius: '8px', fontWeight: '600' }}
                    >
                        <i className="bi bi-printer"></i> {printing ? 'Generando...' : 'Imprimir / PDF'}
                    </button>
                    {!isViewOnly && (
                        <button 
                            type="button" 
                            onClick={handleSubmit} 
                            disabled={saving}
                            className="btn btn-sm text-white d-flex align-items-center gap-1 shadow-sm"
                            style={{ backgroundColor: '#111827', color: '#ffffff', border: '1px solid #111827', borderRadius: '8px', fontWeight: '700' }}
                        >
                            <i className="bi bi-check-lg" style={{ color: '#ffffff' }}></i> 
                            <span style={{ color: '#ffffff' }}>{saving ? 'Guardando...' : 'Guardar Ficha'}</span>
                        </button>
                    )}
                </div>
            </div>

            {/* Hoja Clínica */}
            {renderSheet()}
        </div>
    );
};

export default HistoriaClinicaVisual;
